import React, { useEffect } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import BaseModal from "@reusables/Modals/BaseModal";
import BaseDropdown from "@reusables/dropdowns/BaseDropdown";
import { Customer } from "@/types/general";
import BaseMaterialButton from "@reusables/BaseMaterialButton";
import UIButton from "@reusables/UIButton";

const formScheme = z.object({
    customer: z.object({
        id: z.number(),
        name: z.string(),
        code: z.string(),
    }),
});

type FormTyping = z.infer<typeof formScheme>;

interface MutationCustomerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCustomerAddition: (customer: Customer.Slim) => void;
    customersOptions: Customer.Slim[];
    isCustomersOptionsLoading: boolean;
}

export default function MutationCustomerModal({
    isOpen,
    onClose,
    onCustomerAddition,
    customersOptions,
    isCustomersOptionsLoading,
}: MutationCustomerModalProps) {
    const { t } = useTranslation("", { keyPrefix: "customerGroups.mutation.modals.customer" });

    const { control, handleSubmit, reset } = useForm<FormTyping>({
        resolver: zodResolver(formScheme),
    });

    const selectedCustomer = useWatch({
        control,
        name: "customer",
    });

    useEffect(() => {
        if (!isOpen) {
            reset();
        }
    }, [isOpen, reset]);

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSubmit = handleSubmit((data) => {
        onCustomerAddition(data.customer);
        reset();
    });

    return (
        <BaseModal isOpen={isOpen} onClose={handleClose} width={620} padding="56px" useCloseIcon>
            <form className="space-y-[32px]" onSubmit={onSubmit}>
                <div className="modal-title">{t("addCustomer")}</div>

                <Controller
                    name="customer"
                    control={control}
                    render={({ field, fieldState }) => (
                        <BaseDropdown
                            {...field}
                            {...fieldState}
                            label={t("dropdowns.customer.label") + " *"}
                            placeholder={t("dropdowns.customer.placeholder")}
                            options={customersOptions}
                            getter={{
                                label: (customer) => customer.name,
                                key: (customer) => customer.id,
                                caption: (customer) => customer.code,
                            }}
                            virtualize
                            autocomplete
                            isLoading={isCustomersOptionsLoading}
                        />
                    )}
                />

                <UIButton
                    type="submit"
                    disabled={!selectedCustomer}
                    size={"lg"}
                    className={"w-full"}
                >
                    {t("buttons.add")}
                </UIButton>
            </form>
        </BaseModal>
    );
}
